export type GameLogColumn = {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  mobileHidden?: boolean;
};

export type GameLogSortOption = {
  value: string;
  label: string;
  category: 'performance' | 'stats' | 'info';
};

export const SORT_OPTIONS: GameLogSortOption[] = [
  { value: 'fantasy_points', label: 'PTS', category: 'performance' },
  { value: 'goals', label: 'Goals', category: 'stats' },
  { value: 'shots', label: 'Shots', category: 'stats' },
  { value: 'shots_on_target', label: 'SOT', category: 'stats' },
  { value: 'sca', label: 'SC', category: 'stats' },
  { value: 'gca', label: 'GC', category: 'stats' },
  { value: 'prog_passes', label: 'IP', category: 'stats' },
  { value: 'prog_carries', label: 'IC', category: 'stats' },
  { value: 'dispossessed', label: 'DIS', category: 'stats' },
  { value: 'match_date', label: 'Date', category: 'info' },
  { value: 'name', label: 'Name', category: 'info' },
];

// Admin (full) column set — used on GameLogLeaderboardPage.
export const STAT_COLUMNS: GameLogColumn[] = [
  { key: 'player_name', label: 'Player' },
  { key: 'current_team_name', label: 'Team', align: 'center' },
  { key: 'primary_position', label: 'Pos', align: 'center', mobileHidden: true },
  { key: 'match_date', label: 'Date', align: 'center' },
  { key: 'opponent', label: 'Opp', align: 'center' },
  { key: 'fantasy_points', label: 'PTS', align: 'right' },
  { key: 'goals', label: 'GL', align: 'right' },
  { key: 'shots_on_target', label: 'SOT', align: 'right', mobileHidden: true },
  { key: 'shots', label: 'SH', align: 'right', mobileHidden: true },
  { key: 'gca', label: 'GC', align: 'right', mobileHidden: true },
  { key: 'sca', label: 'SC', align: 'right', mobileHidden: true },
  { key: 'prog_passes', label: 'IP', align: 'right', mobileHidden: true },
  { key: 'prog_carries', label: 'IC', align: 'right', mobileHidden: true },
  { key: 'dispossessed', label: 'DIS', align: 'right', mobileHidden: true },
];

// Contest (trimmed) column set — used in ContestGameLogsSection.
// Desktop shows the full admin set + Salary. Mobile shows Player / Team / Pos / PTS / Opp / Sal
// (PTS is surfaced 4th so it lands in the initial mobile viewport without horizontal scroll).
export const CONTEST_STAT_COLUMNS: GameLogColumn[] = [
  { key: 'player_name', label: 'Player' },
  { key: 'current_team_name', label: 'Team', align: 'center' },
  { key: 'primary_position', label: 'Pos', align: 'center' },
  { key: 'fantasy_points', label: 'PTS', align: 'right' },
  { key: 'match_date', label: 'Date', align: 'center', mobileHidden: true },
  { key: 'opponent', label: 'Opp', align: 'center' },
  { key: 'dfs_salary', label: 'Sal', align: 'right' },
  { key: 'goals', label: 'GL', align: 'right', mobileHidden: true },
  { key: 'shots_on_target', label: 'SOT', align: 'right', mobileHidden: true },
  { key: 'shots', label: 'SH', align: 'right', mobileHidden: true },
  { key: 'gca', label: 'GC', align: 'right', mobileHidden: true },
  { key: 'sca', label: 'SC', align: 'right', mobileHidden: true },
  { key: 'prog_passes', label: 'IP', align: 'right', mobileHidden: true },
  { key: 'prog_carries', label: 'IC', align: 'right', mobileHidden: true },
  { key: 'dispossessed', label: 'DIS', align: 'right', mobileHidden: true },
];

export const SORTABLE_KEYS = new Set(SORT_OPTIONS.map(opt => opt.value));

// Contest-specific sort options add `dfs_salary`. Kept separate so the admin
// Game Logs page doesn't surface a "Salary" sort it has no data for.
export const CONTEST_SORT_OPTIONS: GameLogSortOption[] = [
  ...SORT_OPTIONS,
  { value: 'dfs_salary', label: 'Salary', category: 'stats' },
];
export const CONTEST_SORTABLE_KEYS = new Set(CONTEST_SORT_OPTIONS.map(opt => opt.value));

// Map display-column keys to sort_by param names (most are the same, except player_name -> name)
export const COLUMN_TO_SORT_KEY: Record<string, string> = {
  player_name: 'name',
};

export const formatShortDate = (dateStr: string): string => {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
};
